import { useCallback } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router';

import { localStorage } from '@change-corgi/core/storage';
import { useNavigate } from '@change-corgi/core-react/router';
import { useTracking } from '@change-corgi/core-react/tracking';
import type { UtilityContext } from '@change-corgi/core-react/utilityContext';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { useStartAPetitionPageData, useStartAPetitionPageState } from '#src/app/pages/startAPetitionV2/pageContext';
import { deleteDraftAuthor } from '#src/app/shared/api/petitionDraft';
import { useCountryCode } from '#src/app/shared/hooks/l10n';
import { useQueryParamValue } from '#src/app/shared/hooks/location';
import { useLoginState } from '#src/app/shared/hooks/session';
import { isLoaded, isLoading } from '#src/app/shared/utils/async';
import { buildStartStrongBaseAttrs, recordRedirectResolved } from '#src/app/shared/utils/startStrong';

import { claimSocialStarterPetitionAngles } from './api/claimSocialStarterPetitionAngles';
import type { PublishedPetitionResponse } from './api/createPublishedPetition';
import { createPublishedPetition } from './api/createPublishedPetition';
import { usePrePublishTracking } from './tracking';
import {
	isAmbassadorSourceLocation,
	resolveIsStartStrongAfterSapEnabled,
	resolvePostPublishDestination,
	resolvePostPublishPath,
	resolvePublishContext,
} from './utils';

type LoginState = ReturnType<typeof useLoginState>;
type Draft = NonNullable<Exclude<ReturnType<typeof useStartAPetitionPageState>['data']['draft'], 'input' | 'metadata'>>;

type UsePrePublishReturn = ModelHookResult<
	{ isLoading: boolean; loginState: LoginState; error: Error | null },
	{ back: () => void; submit: () => void }
>;

type StartStrongAfterSapExperiment = {
	experimentName: string;
	variation: string;
	alreadyTreated: boolean;
	override: true | undefined;
	fallback: true | undefined;
};

/**
 * Everything that happens after a successful publish: resolving the
 * starter_experience_start_strong_after_sap exposure/redirect and the (independent) social starter
 * angle claim run concurrently, then tracking, then navigating. Pulled out of the mutation's
 * `onSuccess` purely to keep `usePrePublish` under the line-count limit.
 */
async function handlePublishSuccess({
	petition,
	resolvedSourceLocation,
	draft,
	tracking,
	utilityContext,
	queryClient,
	navigate,
	isAmbassadorPostCreateFlowEnabled,
	startStrongAfterSap,
	countryCode,
}: {
	petition: PublishedPetitionResponse;
	resolvedSourceLocation: string | undefined;
	draft: Draft;
	tracking: ReturnType<typeof usePrePublishTracking>;
	utilityContext: UtilityContext;
	queryClient: QueryClient;
	navigate: (path: string) => void;
	isAmbassadorPostCreateFlowEnabled: boolean;
	startStrongAfterSap: StartStrongAfterSapExperiment;
	countryCode: string;
}): Promise<void> {
	const angleToken = draft.metadata?.angleToken;
	// Explicit `=== false` (not `!videoConsent`): a missing/stale value (e.g. an old draft
	// started before this checkbox existed) must fail safe toward keeping source_url, never
	// toward clearing it.
	const clearSourceUrl = draft.metadata?.videoConsent === false;

	// Run concurrently with the social starter angle claim below: neither depends on the other's
	// result (the claim only needs angleToken/petition.id/clearSourceUrl, all already available),
	// so awaiting them sequentially would add the claim's own timeout on top of this resolution's
	// for no reason — worth avoiding since both are on a first-time publisher's critical path.
	//
	// Still resolved (and, if eligible, treated) before any tracking fires below: treatExperiment
	// patches the session's experiment_variations tracking property asynchronously, via a
	// read-modify-write scoped to its own request/response. Firing
	// trackPrePublishActivatePetition/trackConversion first would race that write, so
	// activate_petition could be sent without the new arm attached — the same sequencing concern
	// documented for other treat-then-track call sites in this codebase.
	const [startStrongAfterSapResolution] = await Promise.all([
		resolveIsStartStrongAfterSapEnabled({
			experimentName: startStrongAfterSap.experimentName,
			variation: startStrongAfterSap.variation,
			alreadyTreated: startStrongAfterSap.alreadyTreated,
			override: startStrongAfterSap.override,
			fallback: startStrongAfterSap.fallback,
			// Deliberately the raw source-location check, not the flag-gated `isAmbassadorPetitionRedirect`
			// used for the actual redirect below: an ambassador petition must stay out of this experiment
			// even when `ambassadorPostCreateFlow` is off, since that flag only controls whether the
			// dedicated ambassador-onboarding redirect fires — it says nothing about eligibility for a
			// different, unrelated experiment.
			isAmbassadorPetition: isAmbassadorSourceLocation({ resolvedSourceLocation }),
			petitionId: petition.id,
			utilityContext,
			trackTreatExperimentTimeout: tracking.trackTreatExperimentTimeout,
		}),
		angleToken
			? // Awaited (not fire-and-forget): the claim also repoints the social starter's vanity
				// URL and staffs the petition server-side. If we navigated before this settled, the
				// starter onboarding page's isSocialStarter check could race a not-yet-staffed 404
				// and cache a false negative for 30 minutes, permanently hiding the gated share step.
				claimSocialStarterPetitionAngles({ token: angleToken, petitionId: petition.id, clearSourceUrl, utilityContext })
			: Promise.resolve(),
	]);

	tracking.trackPrePublishActivatePetition({ petition, draft, sourceLocation: resolvedSourceLocation });
	tracking.trackConversion({ petition });

	deleteDraftAuthor({ storage: localStorage, queryClient });

	const destination = resolvePostPublishDestination({
		resolvedSourceLocation,
		isAmbassadorPostCreateFlowEnabled,
		isStartStrongAfterSapEnabled: startStrongAfterSapResolution.enabled,
	});

	// Every publish records this, not only eligible ones, so the redirect dashboard sees the whole
	// distribution of outcomes.
	recordRedirectResolved({
		...buildStartStrongBaseAttrs({
			petitionId: petition.id,
			petitionSlug: petition.slug,
			countryCode,
			experimentVariant: startStrongAfterSap.variation,
			gqlOperation: 'TreatExperiment',
			flowKind: 'redirect',
		}),
		destination,
		treatOutcome: startStrongAfterSapResolution.treatOutcome,
		resolveLatencyMs: startStrongAfterSapResolution.resolveLatencyMs,
	});

	navigate(resolvePostPublishPath({ petition, destination }));
}

export function usePrePublish(): UsePrePublishReturn {
	const { data, actions } = useStartAPetitionPageState();
	const { data: pageData } = useStartAPetitionPageData();

	const isKickstartExperimentEnabled = pageData.featureConfigs.petitionKickstartToggle;
	const isAmbassadorPostCreateFlowEnabled = pageData.featureConfigs.ambassadorPostCreateFlow;
	const {
		experimentName: startStrongAfterSapExperimentName,
		variation: startStrongAfterSapVariation,
		treated: startStrongAfterSapAlreadyTreated,
		override: startStrongAfterSapOverride,
		fallback: startStrongAfterSapFallback,
	} = pageData.experiments.starterExperienceStartStrongAfterSap;

	const utilityContext = useUtilityContext();
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const track = useTracking();
	const loginState = useLoginState();
	const { pathname, search } = useLocation();
	const sourceLocation = useQueryParamValue('source_location');
	const countryCode = useCountryCode();

	const tracking = usePrePublishTracking();

	const {
		mutate: publishPetition,
		error: publishError,
		...mutationState
	} = useMutation({
		mutationFn: async (draft: Draft) => {
			// Read the Social Starter's server-owned staffing attribution and matched decision maker
			// fresh at publish time. Non-Social-Starter drafts preserve the URL attribution and have no DM.
			const { sourceLocation: resolvedSourceLocation, decisionMakerId } = await resolvePublishContext({
				angleToken: draft.metadata?.angleToken,
				urlSourceLocation: sourceLocation,
				utilityContext,
			});
			const petition = await createPublishedPetition(
				{
					petition: draft,
					isKickstartExperimentEnabled,
					sourceLocation: resolvedSourceLocation,
					decisionMakerId,
				},
				utilityContext,
			);
			// Return the resolved source_location alongside the petition so tracking reports exactly what
			// was published (the metadata-derived code for SS drafts), not the possibly-lossy URL param.
			return { petition, resolvedSourceLocation };
		},
		onMutate: () => {
			void track('sap_step_submit', { step: 'prepublish', step_data: {}, step_data_raw: '{}' });
		},
		onSuccess: async ({ petition, resolvedSourceLocation }, draft: Draft) =>
			handlePublishSuccess({
				petition,
				resolvedSourceLocation,
				draft,
				tracking,
				utilityContext,
				queryClient,
				navigate,
				isAmbassadorPostCreateFlowEnabled,
				startStrongAfterSap: {
					experimentName: startStrongAfterSapExperimentName,
					variation: startStrongAfterSapVariation,
					alreadyTreated: startStrongAfterSapAlreadyTreated,
					override: startStrongAfterSapOverride,
					fallback: startStrongAfterSapFallback,
				},
				countryCode,
			}),
		onError: (error) => {
			const err = error instanceof Error ? error : new Error('SAP: Failed to save petition');
			void track('activate_petition_error', { context: 'prepublish', error_type: err.message });
		},
	});

	const back = useCallback(() => {
		void track('sap_step_back', { step: 'prepublish' });

		actions.send({ type: 'back' });
	}, [actions, track]);

	const submit = useCallback(() => {
		if (loginState === 'AUTHENTICATED' && data.draft) {
			publishPetition(data.draft);
		} else {
			navigate(`/login_or_join?redirectTo=${encodeURIComponent(`${pathname}${search}`)}&user_flow=sap`);
		}
	}, [data.draft, publishPetition, navigate, loginState, pathname, search]);

	return {
		data: {
			isLoading: isLoading(mutationState) || isLoaded(mutationState),
			loginState,
			error: publishError,
		},
		actions: {
			back,
			submit,
		},
	};
}
